import { useId, useState, type ReactNode } from 'react'

import {
  AUDIENCE_SEGMENTS,
  AUTOMATION_ACTIONS,
  AUTOMATION_TRIGGERS,
  type AutomationAction,
  type AutomationRule,
  type CampaignAudience,
} from '@/api/campaigns'
import { cx } from '@/design'

import type { DraftIssue } from './wizard/state'

export interface ControlProps {
  id: string
  'aria-invalid'?: true
  'aria-describedby'?: string
}

/** A labelled control with its hint and its error, wired up for assistive technology. */
export function Field({
  label,
  error,
  hint,
  wide,
  children,
}: {
  label: string
  error?: string | null
  hint?: ReactNode
  wide?: boolean
  children: (props: ControlProps) => ReactNode
}) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ')
  return (
    <div className={cx('camp-field', wide && 'camp-form__wide')}>
      <label htmlFor={id}>{label}</label>
      {children({ id, ...(error ? { 'aria-invalid': true as const } : {}), ...(describedBy ? { 'aria-describedby': describedBy } : {}) })}
      {hint ? (
        <span id={hintId} className="camp-hint">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className="camp-error">
          {error}
        </span>
      ) : null}
    </div>
  )
}

/** An amount typed into a number field: blank is "not set", never zero. */
export function parseAmount(value: string): number | null {
  if (value.trim() === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function amountValue(value: number | null): string {
  return value === null ? '' : String(value)
}

const SEVERITY_TEXT = { error: 'Fix', required: 'Needed to launch', warning: 'Check' } as const

export function IssueList({ issues, label }: { issues: readonly DraftIssue[]; label: string }) {
  if (issues.length === 0) return null
  return (
    <ul className="camp-issues" aria-label={label}>
      {issues.map((issue) => (
        <li key={`${issue.step}:${issue.field}:${issue.message}`} data-severity={issue.severity}>
          <strong>{SEVERITY_TEXT[issue.severity]}:</strong> {issue.message}
        </li>
      ))}
    </ul>
  )
}

function toggle(list: readonly string[], value: string, on: boolean): string[] {
  return on ? [...list.filter((item) => item !== value), value] : list.filter((item) => item !== value)
}

function places(text: string): string[] {
  return text
    .split(/[,\n]/)
    .map((place) => place.trim())
    .filter(Boolean)
}

/** Included and excluded segments, and optional places. Shared by the create flow and the campaign page. */
export function AudienceFields({
  audience,
  onChange,
  error,
  geographyError,
}: {
  audience: CampaignAudience
  onChange: (patch: Partial<CampaignAudience>) => void
  error?: string | null
  geographyError?: string | null
}) {
  // The typed text is kept as typed, so a trailing comma is not swallowed mid-word.
  const [geography, setGeography] = useState(audience.geography.join(', '))
  return (
    <>
      <fieldset className="camp-fieldset">
        <legend>Include</legend>
        <div className="camp-choices">
          {AUDIENCE_SEGMENTS.map((segment) => (
            <label key={segment.id} className="camp-choice">
              <input
                type="checkbox"
                checked={audience.include.includes(segment.id)}
                onChange={(event) => onChange({ include: toggle(audience.include, segment.id, event.target.checked) })}
              />
              <span>{segment.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="camp-fieldset">
        <legend>Exclude</legend>
        <div className="camp-choices">
          {AUDIENCE_SEGMENTS.map((segment) => (
            <label key={segment.id} className="camp-choice">
              <input
                type="checkbox"
                aria-label={`Exclude ${segment.label}`}
                checked={audience.exclude.includes(segment.id)}
                onChange={(event) => onChange({ exclude: toggle(audience.exclude, segment.id, event.target.checked) })}
              />
              <span>{segment.label}</span>
            </label>
          ))}
        </div>
        {error ? <span className="camp-error">{error}</span> : null}
      </fieldset>
      <Field label="Geography (optional)" hint="Towns, cities or regions, separated by commas." error={geographyError}>
        {(props) => (
          <input
            {...props}
            value={geography}
            placeholder="Manchester, Leeds"
            onChange={(event) => {
              setGeography(event.target.value)
              onChange({ geography: places(event.target.value) })
            }}
          />
        )}
      </Field>
    </>
  )
}

/** Optional triggers, each with the action it takes. */
export function AutomationFields({ rules, onChange }: { rules: readonly AutomationRule[]; onChange: (rules: AutomationRule[]) => void }) {
  return (
    <fieldset className="camp-fieldset">
      <legend>Triggers</legend>
      <div className="camp-panels">
        {AUTOMATION_TRIGGERS.map((trigger) => {
          const rule = rules.find((candidate) => candidate.trigger === trigger.key)
          return (
            <div key={trigger.key} className="camp-choice">
              <input
                id={`trigger-${trigger.key}`}
                type="checkbox"
                checked={Boolean(rule)}
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? [...rules, { trigger: trigger.key, action: 'notify_team' }]
                      : rules.filter((candidate) => candidate.trigger !== trigger.key),
                  )
                }
              />
              <span className="camp-choice__text">
                <label htmlFor={`trigger-${trigger.key}`}>{trigger.label}</label>
                {rule ? (
                  <select
                    aria-label={`Action when ${trigger.label.toLowerCase()}`}
                    value={rule.action}
                    onChange={(event) =>
                      onChange(
                        rules.map((candidate) =>
                          candidate.trigger === trigger.key ? { ...candidate, action: event.target.value as AutomationAction } : candidate,
                        ),
                      )
                    }
                  >
                    {AUTOMATION_ACTIONS.map((action) => (
                      <option key={action.key} value={action.key}>
                        {action.label}
                      </option>
                    ))}
                  </select>
                ) : null}
              </span>
            </div>
          )
        })}
      </div>
    </fieldset>
  )
}
